import { z } from "zod";

export type CaseFieldSection = "Client" | "Incident" | "Medical" | "Insurance" | "Other";

export type CaseFieldTarget = {
    key: string;
    label: string;
    section: CaseFieldSection;
    required: boolean;
};

export const CASE_TYPE_TARGET_KEY = "caseType";

export const INCIDENT_STATE_TARGET_KEY = "incidentState";

export const CASE_FIELD_TARGETS: CaseFieldTarget[] = [
    { key: "clientFirstName", label: "Client First Name", section: "Client", required: true },
    { key: "clientLastName", label: "Client Last Name", section: "Client", required: true },
    { key: "clientPhone", label: "Client Phone", section: "Client", required: true },
    { key: "clientEmail", label: "Client Email", section: "Client", required: true },
    { key: "dateOfIncident", label: "Date of Incident", section: "Incident", required: true },
    {
        key: "incidentLocation",
        label: "Location of Accident",
        section: "Incident",
        required: false,
    },
    {
        key: "accidentDescription",
        label: "Accident Description",
        section: "Incident",
        required: false,
    },
    { key: "treatment", label: "Treatment", section: "Medical", required: false },
    { key: "healthInsurance", label: "Health Insurance", section: "Medical", required: false },
    { key: "clientInsurance", label: "Client Insurance", section: "Insurance", required: false },
    { key: "atFaultInsurance", label: "At-Fault Insurance", section: "Insurance", required: false },
    { key: "propertyDamage", label: "Property Damage", section: "Insurance", required: false },
    { key: "additionalComments", label: "Additional Comments", section: "Other", required: false },
    { key: "clientAddress", label: "Client Address", section: "Client", required: false },
    { key: "county", label: "County", section: "Incident", required: false },
    {
        key: "accidentReportDone",
        label: "Accident Report Done",
        section: "Incident",
        required: false,
    },
    { key: "passengers", label: "Passengers", section: "Incident", required: false },
    { key: "emsUsed", label: "EMS", section: "Medical", required: false },
    { key: CASE_TYPE_TARGET_KEY, label: "Case Type", section: "Incident", required: true },
    {
        key: INCIDENT_STATE_TARGET_KEY,
        label: "Incident State",
        section: "Incident",
        required: false,
    },
];

export const parsedLeadFieldSchema = z.object({
    id: z.string(),
    sourceLabel: z.string(),
    sourceValue: z.string(),
    suggestedTarget: z.string().nullable(),
});

export type ParsedLeadField = z.infer<typeof parsedLeadFieldSchema>;

export const SYSTEM_ACTOR = "System";

export const WITHDRAWN_CASE_STATUS = "Withdrawn";

export const pendingLeadStatusSchema = z.enum(["pending", "created", "declined"]);

export type PendingLeadStatus = z.infer<typeof pendingLeadStatusSchema>;

export const pendingLeadSchema = z.object({
    receivedAt: z.string(),
    leadIdentifier: z.string(),
    vendor: z.string(),
    detectedName: z.string().nullable(),
    detectedType: z.string().nullable(),
    status: pendingLeadStatusSchema,
    parsedFields: z.array(parsedLeadFieldSchema),
    declineReason: z.string().nullable(),
    declinedBy: z.string().nullable(),
    actionedAt: z.string().nullable(),
    actionedBy: z.string().nullable(),
    caseUUID: z.string().nullable(),
    caseStatus: z.string().nullable().optional(),
    caseNumber: z.string().nullable(),
    partnerUUID: z.string().nullable(),
    partnerName: z.string().nullable(),
    rawPayload: z.union([z.string(), z.record(z.string(), z.unknown())]).nullable(),
});

export const pendingLeadLogSchema = z.object({
    at: z.string(),
    action: z.enum(["created", "declined", "rejected", "reactivated"]),
    by: z.string(),
    reason: z.string().nullable(),
    pendingLeadUUID: z.string(),
    leadLabel: z.string(),
});

export const PENDING_LEAD_STATUS_LABELS: Record<PendingLeadStatus, string> = {
    pending: "Pending Review",
    created: "Approved",
    declined: "Declined",
};
